import React, { useMemo, useCallback, useState, useEffect } from 'react';
import SvgIcon from '@/pages/home/components/SvgIcon';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import CommonBubble from '@home/components/CommonBubble';
import type { ICascadeFilterComponentProps } from './_type';
import type { RegionSelectValueType } from '@/pages/home/components/NewRegionSelect/type';
import {
  getParentChildKeys,
  convertToRegionSelectValue,
  convertToRegionSelectOptions,
  calculateResultValue,
  getLanguageConfig,
  formatRadioOptions,
  calculateRealRegionsList,
  convertFromRegionSelectValue,
} from './_utils';
import classNames from 'classnames';
import './index.scss';

const CASCADE_LAYOUT_OFFSET = 271;
const CASCADE_MIN_CONTAINER_WIDTH = 540;

const CascadeFilterComponent: React.FC<ICascadeFilterComponentProps> = (props) => {
  const {
    filterKey,
    value,
    options: propsOptions,
    onChange,
    locale,
    name,
    allInfo,
    lftName,
    rtName,
    parentKey: configParentKey,
    childKey: configChildKey,
    radioOptions = [],
    renderKey,
    className = '',
    cascadeBoxClassName,
    getLocalText,
    parentChildName,
    tooltip,
    childPanelWidth,
    optionDisplayOrder,
    ...restProps
  } = props;

  // hover 状态，用于控制 infoIcon 的图标样式
  const [isHover, setIsHover] = useState(false);
  // TODO: 后续如需在下拉框展开时隐藏 tooltip，可放开 isOpen 状态
  // const [isOpen, setIsOpen] = useState(false);

  const options = useMemo(() => {
    return propsOptions?.filter((item) => item?.type !== '' && item?.code !== '');
  }, [propsOptions]);

  // 处理鼠标悬停事件 - 使用 useCallback 避免内存泄漏
  const handleMouseOver = useCallback((): void => {
    setIsHover(true);
  }, []);

  const handleMouseOut = useCallback((): void => {
    setIsHover(false);
  }, []);

  useEffect(() => {
    return () => setIsHover(false); // 清理状态
  }, [options]);

  // 创建 infoIcon 的辅助函数 - 使用 useCallback 优化性能
  const createInfoIcon = useCallback((infoText: string | null | undefined): React.ReactElement | null => {
    if (!infoText) return null;
    try {
      return (
        <span style={{ display: 'flex', alignItems: 'center' }}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          <CommonBubble
            style={{ left: -18 }}
            placement='bottom-start'
            content={infoText}
            dark
          >
            {/* 此处必须包一层，不然点击不生效 */}
            <i><SvgIcon className='select-header-ic' type={isHover ? 'alert-circle-action' : 'alert-circle-m'} /></i>
          </CommonBubble>
        </span>
      );
    } catch (error) {
      console.error('创建信息图标失败:', error);
      return null;
    }
  }, [handleMouseOver, handleMouseOut]);

  // 根据选项格式自动推断 parentKey 和 childKey
  // 注意：后端暂时不支持 parentKey 和 childKey 配置，前端做默认处理
  // 1. 子元素的 childKey 直接用对应筛选器的 key（如 ua_network 筛选器，childKey 就是 'ua_network'）
  // 2. parentKey 当前默认用 'parent'
  // 3. 传参也没有规划父元素相关的传参，只传子元素的值（通过 formatFilterValues 处理）
  const { parentKey, childKey } = useMemo(() => {
    return getParentChildKeys(configParentKey, configChildKey, options, filterKey);
  }, [configParentKey, configChildKey, options, filterKey]);

  // 将值格式从筛选器系统转换为 NewRegionSelect 格式
  const regionSelectValue = useMemo(() => {
    return convertToRegionSelectValue(value, parentKey, childKey);
  }, [value, parentKey, childKey]);

  /** 子项顺序由 mapCascadeFields（CASCADE_FILTER_MSG_MAP / 后端）注入 optionDisplayOrder */
  const regionSelectOptions = useMemo(() => {
    return convertToRegionSelectOptions(options, optionDisplayOrder);
  }, [options, optionDisplayOrder]);

  // 计算最终的值（使用 getRegionSelectedData 处理）
  const resultValue = useMemo(() => {
    return calculateResultValue(regionSelectOptions, regionSelectValue);
  }, [regionSelectOptions, regionSelectValue]);

  // 语言配置
  const language = useMemo(() => {
    return getLanguageConfig(name, lftName, rtName, parentChildName);
  }, [name, lftName, rtName, parentChildName]);

  // 处理值更改 - 转换回筛选器系统格式
  const handleChange = useCallback((newValue: (RegionSelectValueType & { region_type?: string; }) | null) => {
    const filterValue = convertFromRegionSelectValue(newValue, filterKey, parentKey, childKey);
    if (filterValue) {
      onChange(filterKey, filterValue);
    }
  }, [filterKey, onChange, parentKey, childKey]);

  // 处理关闭时的回调
  const handleClose = useCallback((newValue: (RegionSelectValueType & { region_type?: string; }) | null) => {
    // TODO: 后续如需在下拉框展开时隐藏 tooltip，可放开 setIsOpen(false)
    handleChange(newValue);
  }, [handleChange]);

  // 处理单选选项格式
  // 参照 RegionSelector 的实现，将 info 字符串转换为 infoIcon React 元素
  const formattedRadioOptions = useMemo(() => {
    return formatRadioOptions(radioOptions, allInfo, name, getLocalText, createInfoIcon);
  }, [radioOptions, allInfo, name, getLocalText, createInfoIcon]);
  // 计算 realRegionsList（NewRegionSelect 需要的 prop）
  const realRegionsList = useMemo(() => {
    return calculateRealRegionsList(regionSelectOptions);
  }, [regionSelectOptions]);

  const cascadeWiderId = useMemo(() => {
    if (!childPanelWidth) return '';
    return `dbd-cascade-wc-${filterKey.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
  }, [childPanelWidth, filterKey]);

  const containerWidth = useMemo(() => {
    if (!childPanelWidth) return 0;
    return Math.max(CASCADE_MIN_CONTAINER_WIDTH, CASCADE_LAYOUT_OFFSET + childPanelWidth);
  }, [childPanelWidth]);

  const cascadeWiderClassName = useMemo(() => {
    if (!childPanelWidth) return '';
    return `dbd-cascade-wider-child ${cascadeWiderId}`;
  }, [childPanelWidth, cascadeWiderId]);

  const rightListWidth = useMemo(() => {
    if (!childPanelWidth) return undefined;
    return Math.min(childPanelWidth, containerWidth - CASCADE_LAYOUT_OFFSET);
  }, [childPanelWidth, containerWidth]);

  return (
    <>
      {containerWidth > 0 && cascadeWiderId && (
        <style>{`.${cascadeWiderId}.dbc-region-select-container{width:${containerWidth}px;min-width:${CASCADE_MIN_CONTAINER_WIDTH}px}`}</style>
      )}
      <CommonBubble
        dark
        escapeWithReference
        flipped={false}
        destroyOnClose={false}
        content={tooltip || null}
      >
        <NewRegionSelect
          language={language}
          key={filterKey}
          className={`search-item ${renderKey || ''} js-search-items-cascade ${className}`}
          boxClassName={classNames('dbd-cascade-selector-box', `dbd-cascade-selector-box-${filterKey}`, cascadeBoxClassName, cascadeWiderClassName)}
          value={resultValue}
          onChange={handleChange}
          onCloseReginSelect={handleClose}
          locale={locale}
          options={regionSelectOptions}
          radioOptions={formattedRadioOptions}
          realRegionsList={realRegionsList}
          closeOnScroll={true}
          shortcutOptions={[]}
          popularList={[]}
          selectFilterType={`${filterKey}_cascade`}
          enableExactOverflowBubble={true}
          {...(rightListWidth ? { rightListWidth } : {})}
          {...restProps}
        />
      </CommonBubble>
    </>
  );
};

export default CascadeFilterComponent;

